import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { useSearchParams } from "react-router-dom";
import DestinationIcon from "../../../assets/offers/destination.svg";
import DateIcon from "../../../assets/offers/date.svg";
import FilterIcon from "../../../assets/offers/filter.svg";
import RoomsIcon from "../../../assets/offers/rooms.svg";
import { GuestPicker } from "./GuestPicker";
import { useSearchStore } from "../../../hooks/useSearchStore";
import { useSuggestedPlaces } from "../../../hooks/useSuggestedPlaces";
import { useMatchBreakpoint } from "../../../hooks/useMatchBreakpoint";

import DateRangePicker from "../../../components/DateRangePicker/DateRangePicker";
import { today, getLocalTimeZone, parseDate } from "@internationalized/date";

import styles from "./Search.module.css";
import { ENV } from "../../../env";
import { addLeadingZero } from "../../../utils/addLeadingZero";
import classNames from "classnames";
import { passionFilters, luxuryBrands, premiumBrands, citiesIdsMap } from "../constants";
import CloseIcon from "../../../assets/common/close-icon.svg";

const Search = ({
  handleSearchChange,
  offerData,
  fetchOfferData,
  propertiesLoading,
  setSkipInitialization,
  onMoreFilters,
  selectedFilters = {},
  onFiltersChange,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const startDateParam = searchParams.get("start_date");
  const endDateParam = searchParams.get("end_date");
  const placeIdParam = searchParams.get("place_id");
  const placeNameParam = searchParams.get("place_name");
  const destinationOverrideParam = searchParams.get("destination_override");
  const [isDatepickerOpened, setIsDatepickerOpened] = useState(false);
  const { showRoomsGuestsFilter } = ENV.FEATURE_FLAGS;

  // Parse URL query parameters
  const getInitialDateRange = () => {
    // Function to convert MM-DD-YYYY to YYYY-MM-DD format
    const convertToISOFormat = (dateStr) => {
      if (!dateStr) return null;

      // Check if the date is in MM-DD-YYYY format
      const mmDdYyyyRegex =
        /^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])-([0-9]{4})$/;
      if (mmDdYyyyRegex.test(dateStr)) {
        const [month, day, year] = dateStr.split("-");
        return `${year}-${month}-${day}`;
      }

      return dateStr; // Return original if not matching MM-DD-YYYY
    };

    try {
      // If both start and end dates are provided in the URL
      if (startDateParam && endDateParam) {
        // Convert dates from MM-DD-YYYY to YYYY-MM-DD format
        const startIso = convertToISOFormat(startDateParam);
        const endIso = convertToISOFormat(endDateParam);

        // Only proceed if conversion was successful
        if (startIso && endIso) {
          try {
            // Parse the converted date strings
            const start = parseDate(startIso);
            const end = parseDate(endIso);

            // Validate dates (ensure start is not after end and not before today)
            const todayDate = today(getLocalTimeZone());

            if (start && end && start <= end) {
              // If start date is in the past, use today instead
              const validStart = start < todayDate ? todayDate : start;
              return { start: validStart, end };
            }
          } catch (parseError) {
            console.error("Error parsing date strings:", parseError);
            // Continue to default return
          }
        }
      }
    } catch (error) {
      console.error("Error processing date parameters:", error);
      // Fall back to default if there's an error
    }

    // Default date range if no valid parameters
    return {
      start: today(getLocalTimeZone()),
      end: today(getLocalTimeZone()).add({ days: 3 }),
    };
  };

  // Initialize date range in store from URL parameters or defaults
  useEffect(() => {
    useSearchStore.getState().setDateRange(getInitialDateRange());
  }, [startDateParam, endDateParam]);

  const [placeId, setPlaceId] = useState(null);
  const [searchQuery, setSearchQuery] = useState(
    placeIdParam && placeNameParam ? (placeNameParam ?? "") : "",
  );
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const suggestionsRef = useRef(null);

  // Use the suggestedPlaces hook
  const {
    suggestedPlaces,
    placeDetails,
    loading: placesLoading,
    detailsLoading,
    error: placesError,
    detailsError,
    fetchSuggestedPlaces,
    fetchPlaceDetails,
    formatPlaceName,
    getPlaceId,
  } = useSuggestedPlaces(searchQuery);

  // Handle date range change
  const handleDateRangeChange = (range) => {
    useSearchStore.getState().setDateRange(range);
  };
  const { rooms, roomsText, guests, children, showPicker, toggle } =
    useSearchStore();

  const getDestinationByParam = (destinationOverrideParam) => {
    const lowerCaseMap = Object.fromEntries(
      Object.entries(citiesIdsMap).map(([k, v]) => [k.toLowerCase(), v])
    );
    const key = (destinationOverrideParam ?? '').toLowerCase();
    return lowerCaseMap[key];
  };

  const roomsGuestsSummary = useMemo(() => {
    const roomEnding = rooms > 1 ? "s" : "";
    const guestEnding = guests > 1 ? "s" : "";
    const guestText = rooms > 4 ? "" : `, ${guests} Guest${guestEnding}`;

    return `${roomsText} Room${roomEnding}${guestText}`;
  }, [rooms, roomsText, guests]);

  const closeGuestPicker = useCallback(
    ({ target }) => {
      if (
        showPicker &&
        !isDatepickerOpened &&
        !target.closest(".guest-picker") &&
        target.type !== "reset" // reset button
      ) {
        toggle();
      }
    },
    [showPicker, toggle, isDatepickerOpened],
  );

  const escapeHandler = (e) => {
    if (e.key !== "Escape" || !showPicker) {
      return;
    }
    toggle();
  };

  // Close suggestions dropdown when clicking outside
  const closeSuggestions = useCallback(
    ({ target }) => {
      if (
        showSuggestions &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(target)
      ) {
        setShowSuggestions(false);
      }
    },
    [showSuggestions],
  );

  // Debounce timer ref
  const debounceTimerRef = useRef(null);

  const initialLoadingWithSearch = async () => {
    const placeDetails = await fetchPlaceDetails(placeIdParam);

    if (placeDetails?.location) {
      setSkipInitialization(true);
      const results = await handleSearchChange({
        rooms,
        guests,
        children,
        selectedLocation: {
          latitude: placeDetails.location.latitude,
          longitude: placeDetails.location.longitude,
          city: placeDetails.location.city,
          state: placeDetails.location.state,
          country: placeDetails.location.country,
        },
        placeDetails,
      });
      if (!results.length && placeDetails?.location) {
        // We want to fallback to default if no results for some location
        console.warn(
          "Destination search returned 0 results, falling back to all hotels",
        );
        setSearchQuery("");
        handleSearchChange({
          rooms,
          guests,
          children,
          selectedLocation: null,
          placeDetails: null,
        });
      }
    } else {
      setSkipInitialization(false);
      setSearchQuery("");
      console.warn("Destination search failed, falling back to all hotels");
    }
  };

  useEffect(() => {
    const id = getDestinationByParam(destinationOverrideParam);
    if (destinationOverrideParam && id && id === placeIdParam) {
      initialLoadingWithSearch();
      const params = new URLSearchParams(searchParams);
      setSearchQuery(destinationOverrideParam);
      params.delete('destination_override');
      setSearchParams(params);
    }
  }, [destinationOverrideParam, placeIdParam]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    const id = getDestinationByParam(destinationOverrideParam);
    const params = new URLSearchParams(searchParams);
    if (id) {
      params.set('place_name', destinationOverrideParam);
      params.set('place_id', id);
    } else {
      if (placeIdParam) {
        initialLoadingWithSearch();
      }
      params.delete('destination_override');
    }

    setSearchParams(params);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Watch for searchDestination changes from the store (e.g., from map state click)
  const searchDestination = useSearchStore((state) => state.searchDestination);
  useEffect(() => {
    if (searchDestination) {
      setSearchQuery(searchDestination);
      // Clear the destination from store after using it
      useSearchStore.getState().setSearchDestination("");
    }
  }, [searchDestination]);

  // Debounced search function
  const debouncedSearch = useCallback(
    (value) => {
      if (value.length >= 2) {
        fetchSuggestedPlaces(value);
        setShowSuggestions(true);
      } else {
        setShowSuggestions(false);
      }
    },
    [fetchSuggestedPlaces],
  );

  // Handle search input change
  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer for debounced search
    debounceTimerRef.current = setTimeout(() => {
      debouncedSearch(value);
    }, 300); // 300ms delay
  };

  // Handle suggestion selection
  const handleSuggestionSelect = (place) => {
    const currentPlaceId = getPlaceId(place);
    setSearchQuery(formatPlaceName(place));
    setPlaceId(currentPlaceId);
    setShowSuggestions(false);

    // Fetch place details
    fetchPlaceDetails(currentPlaceId);
  };

  // Update selectedLocation when placeDetails changes or search query is cleared
  useEffect(() => {
    // Priority 1: If search query is empty, clear the location
    if (!searchQuery.trim()) {
      setSelectedLocation(null);
    }
    // Priority 2: If we have place details and a search query, set the location
    else if (placeDetails && placeDetails.location && searchQuery.trim()) {
      setSelectedLocation({
        latitude: placeDetails.location.latitude,
        longitude: placeDetails.location.longitude,
        city: placeDetails.location.city,
        state: placeDetails.location.state,
        country: placeDetails.location.country,
      });
    }
  }, [placeDetails, searchQuery]);

  // Handle place details error
  useEffect(() => {
    if (detailsError) {
      console.error("Error fetching place details:", detailsError);
      // Could show an error message to the user here
    }
  }, [detailsError]);

  useEffect(() => {
    document.addEventListener("click", closeGuestPicker);
    document.addEventListener("click", closeSuggestions);

    document.addEventListener("keydown", escapeHandler);

    return () => {
      document.removeEventListener("click", closeGuestPicker);
      document.removeEventListener("click", closeSuggestions);
      document.removeEventListener("keydown", escapeHandler);
    };
  }, [closeGuestPicker, closeSuggestions]);

  const setQueryParams = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    const state = useSearchStore.getState();
    const range = state.dateRange;
    params.set(
      "start_date",
      range.start
        ? `${addLeadingZero(range.start.month)}-${addLeadingZero(range.start.day)}-${range.start.year}`
        : undefined,
    );
    params.set(
      "end_date",
      range.end
        ? `${addLeadingZero(range.end.month)}-${addLeadingZero(range.end.day)}-${range.end.year}`
        : undefined,
    );
    params.set("place_id", placeId ?? "");
    params.set("place_name", searchQuery);

    // Preserve filter parameters when updating search params
    if (selectedFilters.passions && selectedFilters.passions.length > 0) {
      params.set("passions", selectedFilters.passions.join(","));
    }
    if (
      selectedFilters.luxuryBrands &&
      selectedFilters.luxuryBrands.length > 0
    ) {
      params.set("luxury_brands", selectedFilters.luxuryBrands.join(","));
    }
    if (
      selectedFilters.premiumBrands &&
      selectedFilters.premiumBrands.length > 0
    ) {
      params.set("premium_brands", selectedFilters.premiumBrands.join(","));
    }

    setSearchParams(params);
  }, [searchParams, placeId, searchQuery, selectedFilters]);

  // Helper function to get filter display name
  const getFilterDisplayName = (category, filterId) => {
    let filterItem = null;
    let prefix = "";

    if (category === "passions") {
      filterItem = passionFilters.find((f) => f.id === filterId);
      prefix = "Passion";
    } else if (category === "luxuryBrands") {
      filterItem = luxuryBrands.find((f) => f.id === filterId);
      prefix = "Luxury Brand";
    } else if (category === "premiumBrands") {
      filterItem = premiumBrands.find((f) => f.id === filterId);
      prefix = "Premium Brand";
    }

    const displayName = filterItem ? filterItem.name : filterId;
    return `${prefix}: ${displayName}`;
  };

  // Helper function to remove a filter
  const removeFilter = (category, filterId) => {
    if (!onFiltersChange) return;

    const updatedFilters = { ...selectedFilters };
    if (updatedFilters[category]) {
      updatedFilters[category] = updatedFilters[category].filter(
        (id) => id !== filterId,
      );
    }
    onFiltersChange(updatedFilters);
  };

  // Generate filter chips from selectedFilters
  const renderFilterChips = () => {
    const chips = [];

    // Add passion chips
    if (selectedFilters.passions && selectedFilters.passions.length > 0) {
      selectedFilters.passions.forEach((passionId) => {
        chips.push(
          <div key={`passions-${passionId}`} className={styles.filterChip}>
            <div className={styles.filterChipContent}>
              <span className={styles.filterChipText}>
                {getFilterDisplayName("passions", passionId)}
              </span>
            </div>
            <img
              className={styles.filterChipCloseIcon}
              src={CloseIcon}
              alt="Remove filter"
              onClick={() => removeFilter("passions", passionId)}
            />
          </div>,
        );
      });
    }

    // Add luxury brand chips
    if (
      selectedFilters.luxuryBrands &&
      selectedFilters.luxuryBrands.length > 0
    ) {
      selectedFilters.luxuryBrands.forEach((brandId) => {
        chips.push(
          <div key={`luxuryBrands-${brandId}`} className={styles.filterChip}>
            <div className={styles.filterChipContent}>
              <span className={styles.filterChipText}>
                {getFilterDisplayName("luxuryBrands", brandId)}
              </span>
            </div>
            <img
              className={styles.filterChipCloseIcon}
              src={CloseIcon}
              alt="Remove filter"
              onClick={() => removeFilter("luxuryBrands", brandId)}
            />
          </div>,
        );
      });
    }

    // Add premium brand chips
    if (
      selectedFilters.premiumBrands &&
      selectedFilters.premiumBrands.length > 0
    ) {
      selectedFilters.premiumBrands.forEach((brandId) => {
        chips.push(
          <div key={`premiumBrands-${brandId}`} className={styles.filterChip}>
            <div className={styles.filterChipContent}>
              <span className={styles.filterChipText}>
                {getFilterDisplayName("premiumBrands", brandId)}
              </span>
            </div>
            <img
              className={styles.filterChipCloseIcon}
              src={CloseIcon}
              alt="Remove filter"
              onClick={() => removeFilter("premiumBrands", brandId)}
            />
          </div>,
        );
      });
    }

    return chips;
  };

  // Check if there are any active filters
  const hasActiveFilters =
    (selectedFilters.passions && selectedFilters.passions.length > 0) ||
    (selectedFilters.luxuryBrands && selectedFilters.luxuryBrands.length > 0) ||
    (selectedFilters.premiumBrands && selectedFilters.premiumBrands.length > 0);

  // Determine if this is being rendered in the mobile panel
  const isInMobilePanel = useMatchBreakpoint(800);

  const onSearch = async () => {
    setQueryParams();

    if (!selectedLocation) {
      fetchOfferData();
      return;
    }

    await handleSearchChange({
      rooms,
      guests,
      children,
      selectedLocation,
      placeDetails,
    });
  };

  return (
    <div
      className={classNames(
        styles.searchContainer,
        showRoomsGuestsFilter ? "" : styles.noRoomsGuestsFilter,
        isInMobilePanel ? styles.mobileView : "",
        hasActiveFilters ? styles.hasFilters : "",
      )}
    >
      <div className={styles.searchMainRow}>
        <div className={classNames(styles.searchFieldsContainer)}>
          <div
            className={classNames(styles.searchField, styles.destinationField)}
            ref={suggestionsRef}
          >
            <label>
              <img src={DestinationIcon} alt="" />
              Destination
            </label>
            <input
              type="text"
              placeholder="Where can we take you?"
              value={searchQuery}
              onChange={handleSearchInputChange}
              onFocus={() =>
                searchQuery.length >= 2 && setShowSuggestions(true)
              }
            />
            {showSuggestions && (
              <div className={styles.suggestionsDropdown}>
                {placesLoading ? (
                  <div
                    className={classNames(
                      styles.suggestionItem,
                      styles.loading,
                    )}
                  >
                    Loading suggestions...
                  </div>
                ) : placesError ? (
                  <div
                    className={classNames(styles.suggestionItem, styles.error)}
                  >
                    Error loading suggestions
                  </div>
                ) : suggestedPlaces.length > 0 ? (
                  suggestedPlaces.map((place, index) => (
                    <div
                      key={`suggestion-${index}`}
                      className={styles.suggestionItem}
                      onClick={() => handleSuggestionSelect(place)}
                    >
                      {formatPlaceName(place)}
                    </div>
                  ))
                ) : (
                  <div
                    className={classNames(
                      styles.suggestionItem,
                      styles.noResults,
                    )}
                  >
                    No places found
                  </div>
                )}
              </div>
            )}
          </div>
          <div
            className={classNames(styles.searchField, styles.datePickerField)}
          >
            <label>
              <img src={DateIcon} alt="" />
              Stay Dates
            </label>
            <DateRangePicker
              value={useSearchStore((state) => state.dateRange)}
              handleDateRangeChange={handleDateRangeChange}
              minValue={today(getLocalTimeZone())}
              onOpenedChange={setIsDatepickerOpened}
              maxValue={
                offerData?.stayEndDate
                  ? parseDate(offerData.stayEndDate)
                  : undefined
              }
            ></DateRangePicker>
          </div>
          {showRoomsGuestsFilter && (
            <div className={classNames(styles.searchField, "guest-picker")}>
              <label>
                <img src={RoomsIcon} alt="" />
                Rooms & Guests
              </label>
              <button
                className={styles.searchFieldBtn}
                type="button"
                onClick={toggle}
              >
                {roomsGuestsSummary}
              </button>
              {showPicker && <GuestPicker />}
            </div>
          )}
        </div>
        <div
          className={classNames(
            styles.searchProperties,
            showRoomsGuestsFilter ? "" : styles.noRoomsGuestsFilter,
          )}
        >
          <button
            className={styles.searchButton}
            onClick={onSearch}
            disabled={propertiesLoading || detailsLoading}
          >
            {propertiesLoading || detailsLoading
              ? "Loading..."
              : "Search Properties"}
          </button>
        </div>

        {onMoreFilters && (
          <div className={styles.moreFiltersContainer}>
            <button className={styles.searchButton} onClick={onMoreFilters}>
              More Filters
            </button>
          </div>
        )}
      </div>
      {hasActiveFilters && (
        <div className={styles.appliedFiltersContainer}>
          <div className={styles.filtersHeader}>
            <img src={FilterIcon} alt="filter icon" />
            APPLIED FILTERS
          </div>
          <div className={styles.filtersContainer}>{renderFilterChips()}</div>
        </div>
      )}
    </div>
  );
};

export default Search;
